import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
/**
 * Field identities (TS-1840): which field in an external system a CRM-DEFINED custom field
 * (`field_definitions`) corresponds to. RecruitCRM is the only system today; the `system` column
 * is there so a later integration (QuickBooks, Stripe) reuses the table. The ~23 RCRM custom
 * fields the CRM keeps as COLUMNS are not links: they stay in code (`RCRM_CUSTOM_FIELDS`,
 * recruitcrm/maps.ts), so a link to one of their fallback ids is refused.
 *
 * A link works both ways by default: the sync pushes the CRM value (CRM → RCRM) and the RCRM
 * webhook receiver writes RCRM's value back (RCRM → CRM). The external field's name and type are
 * SNAPSHOTS taken when the link is written; both directions compare them with what RCRM says
 * (its live definition on push, the webhook's own entry on receive) and skip a value whose field
 * was renamed or retyped, because a rename and an id shift look the same and an id shift writes
 * into the wrong field.
 */
export const FIELD_IDENTITY_SYSTEMS = ['recruitcrm'];
export const fieldIdentitySystemSchema = z.enum(FIELD_IDENTITY_SYSTEMS);
/**
 * `both` (the default): pushed by the sync AND received from RCRM webhooks. `push`: CRM → RCRM
 * only, for a field the CRM alone may change.
 */
export const FIELD_IDENTITY_DIRECTIONS = ['push', 'both'];
export const fieldIdentityDirectionSchema = z.enum(FIELD_IDENTITY_DIRECTIONS);
/**
 * The RCRM custom-field entities a CRM field may link to. Deals are linkable but RECEIVE-ONLY: the
 * CRM never pushes a deal to RCRM (`rcrmFieldPushes`), so a deal link is always `both`.
 */
export const RCRM_LINKABLE_FIELD_ENTITIES = [
    'candidates',
    'contacts',
    'companies',
    'jobs',
    'deals',
];
/**
 * CRM custom-field entity → RCRM custom-field entity. Projects have no counterpart (they do not
 * exist in RCRM), so their fields stay CRM-only. A CRM deal IS an RCRM deal, but only RECEIVED
 * (`rcrmFieldPushes`): no sync pushes deals. Keyed by
 * the CRM entity name on purpose rather than by `CUSTOM_FIELD_ENTITIES`: which CRM entities can
 * carry custom fields grows independently of which ones the sync writes.
 */
export const CRM_TO_RCRM_FIELD_ENTITY = {
    candidates: 'candidates',
    companies: 'companies',
    contacts: 'contacts',
    jobs: 'jobs',
    deals: 'deals',
};
export const FIELD_IDENTITY_CRM_ENTITIES = Object.keys(CRM_TO_RCRM_FIELD_ENTITY);
export const fieldIdentityCrmEntitySchema = z.enum(FIELD_IDENTITY_CRM_ENTITIES);
/** The RCRM custom-field entity a CRM entity's fields link to, or null when it has none. */
export function rcrmFieldEntityFor(crmEntity) {
    return Object.hasOwn(CRM_TO_RCRM_FIELD_ENTITY, crmEntity)
        ? CRM_TO_RCRM_FIELD_ENTITY[crmEntity]
        : null;
}
/** Whether the CRM → RCRM sync pushes this RCRM entity's linked fields. It never pushes deals. */
export const rcrmFieldPushes = (entity) => entity !== 'deals';
/**
 * RCRM `field_type` values seen in the live workspace (Data Team inventory, 2026-10-29). `file`
 * is listed for completeness and is never linkable.
 */
export const RCRM_FIELD_TYPES = [
    'text',
    'longtext',
    'number',
    'date',
    'date_time',
    'dropdown',
    'multiselect',
    'checkbox',
    'user',
    'email',
    'phonenumber',
    'file',
];
/**
 * `confirmed`: the value format RCRM accepts is known and the sync sends it. `pending`: the link
 * may be written but nothing is sent until a real RCRM write confirms the format — flipping a pair
 * to `confirmed` is the one-line change that turns it on (as in recruitcrm/maps.ts).
 */
export const FIELD_IDENTITY_PAIR_STATUSES = ['confirmed', 'pending'];
export const fieldIdentityPairStatusSchema = z.enum(FIELD_IDENTITY_PAIR_STATUSES);
/**
 * The closed catalogue of linkable type pairs: CRM field type → the RCRM `field_type`s it may link
 * to, each with its status. A CRM type with no entry (`currency`, `reference`, `json`) cannot be
 * linked; no CRM type links to RCRM `file`. Multiselect, user and phonenumber are `pending` FOR THE
 * PUSH: no Relay code writes them, so what RCRM accepts is unobserved. Reading them is separate
 * (`rcrmFieldTypePairReadStatus`): multiselect and phonenumber are read since TS-1960 (formats
 * observed in the v1 API on 2026-11-03). The push flips after ONE confirmed write to an
 * allowlisted test record: change the pair to `confirmed` here.
 */
export const RCRM_FIELD_TYPE_PAIRS = {
    text: { text: 'confirmed', longtext: 'confirmed' },
    long_text: { text: 'confirmed', longtext: 'confirmed' },
    url: { text: 'confirmed', longtext: 'confirmed' },
    phone: { text: 'confirmed', longtext: 'confirmed', phonenumber: 'pending' },
    email: { text: 'confirmed', longtext: 'confirmed', email: 'confirmed' },
    number: { number: 'confirmed', text: 'confirmed' },
    date: { date: 'confirmed' },
    datetime: { date_time: 'confirmed' },
    single_select: { dropdown: 'confirmed' },
    boolean: { checkbox: 'confirmed' },
    multi_select: { multiselect: 'pending' },
    user: { user: 'pending' },
    currency: {},
    reference: {},
    json: {},
};
/**
 * The status of linking a CRM field of `crmType` to an RCRM field of `rcrmType` (compared
 * exactly: the sync compares the snapshot with RCRM's own spelling), or null when the pair is
 * outside the catalogue.
 */
export function rcrmFieldTypePairStatus(crmType, rcrmType) {
    if (!Object.hasOwn(RCRM_FIELD_TYPE_PAIRS, crmType))
        return null;
    const pairs = RCRM_FIELD_TYPE_PAIRS[crmType];
    return Object.hasOwn(pairs, rcrmType) ? pairs[rcrmType] : null;
}
/**
 * The status of READING an RCRM value into a linked CRM field (history import, webhook receiver):
 * `pending` only for `user`, which needs the RCRM user ↔ CRM user map (`planInbound`'s `users`,
 * TS-2002); null when the pair is outside the catalogue.
 */
export function rcrmFieldTypePairReadStatus(crmType, rcrmType) {
    if (rcrmFieldTypePairStatus(crmType, rcrmType) === null)
        return null;
    return rcrmType === 'user' ? 'pending' : 'confirmed';
}
/** Values RCRM is known to use for a ticked box (Relay `TRUE_TOKENS`, minus its field-specific `authorized`). */
const RCRM_TRUE_TOKENS = new Set(['1', 'true', 'yes', 'y', 'on', 'checked']);
/** Whether an RCRM checkbox option or value reads as ticked (trimmed, case-insensitive). */
export const rcrmReadsAsTrue = (label) => RCRM_TRUE_TOKENS.has(label.trim().toLowerCase());
const externalFieldIdSchema = z.number().int().positive().max(2_147_483_647);
const externalFieldNameSchema = z.string().trim().min(1).max(200);
const externalFieldTypeSchema = z.string().trim().min(1).max(64);
/**
 * Link an existing CRM field definition to an RCRM field. The RCRM entity is not sent: it is
 * derived from the definition's entity (`rcrmFieldEntityFor`).
 */
export const fieldIdentityCreateSchema = z.object({
    fieldDefinitionId: idSchema,
    system: fieldIdentitySystemSchema.default('recruitcrm'),
    externalFieldId: externalFieldIdSchema,
    /** RCRM's `field_name` as it reads today (a snapshot the sync checks). */
    externalFieldName: externalFieldNameSchema,
    /** RCRM's `field_type` (a snapshot the sync checks), e.g. `text`, `dropdown`. */
    externalFieldType: externalFieldTypeSchema,
    direction: fieldIdentityDirectionSchema.default('both'),
});
/**
 * Point a link at another RCRM field, refresh its snapshots (e.g. after a rename in RCRM), or
 * change its direction. Pointing it at another field needs that field's name and type in the same
 * body: the old field's snapshots would otherwise describe the new one.
 */
export const fieldIdentityPatchSchema = z
    .object({
    externalFieldId: externalFieldIdSchema,
    externalFieldName: externalFieldNameSchema,
    externalFieldType: externalFieldTypeSchema,
    direction: fieldIdentityDirectionSchema,
})
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' })
    .superRefine((p, ctx) => {
    if (p.externalFieldId === undefined)
        return;
    for (const key of ['externalFieldName', 'externalFieldType'])
        if (p[key] === undefined)
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: `required with externalFieldId: the new field's ${key === 'externalFieldName' ? 'name' : 'type'}`,
            });
});
export const fieldIdentityQuerySchema = z.object({
    entity: fieldIdentityCrmEntitySchema.optional(),
    fieldDefinitionId: idSchema.optional(),
});
/** A link as the API returns it. */
export const fieldIdentitySchema = z.object({
    id: idSchema,
    system: fieldIdentitySystemSchema,
    /** The CRM definition's entity. */
    crmEntity: z.string(),
    fieldDefinitionId: idSchema,
    /**
     * The linked definition, or null when it was retired since: such a link is never used by the
     * sync, and deleting it frees the RCRM field for another definition.
     */
    definition: z.object({ key: z.string(), label: z.string(), type: z.string() }).nullable(),
    externalEntity: z.enum(RCRM_LINKABLE_FIELD_ENTITIES),
    externalFieldId: z.number().int(),
    externalFieldName: z.string(),
    externalFieldType: z.string(),
    direction: fieldIdentityDirectionSchema,
    /** `pending`: linked but not sent until the format is confirmed; null when the definition is retired. */
    pairStatus: fieldIdentityPairStatusSchema.nullable(),
    createdAt: instantSchema,
    updatedAt: instantSchema,
});
//# sourceMappingURL=field-identities.js.map